import type { FC, ReactNode } from 'react'
import type { Client } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { Mark } from '@/components/generals/mark'
import { PlaceholderTag } from '@/components/generals/placeholder-tag'

interface ClientStripProps {
    clients: Client[]
}

/* Text logos stand in until a logo image is uploaded in the CMS. Each gets its own typographic voice. */
const WORDMARK_STYLES = [
    'font-heading font-wide font-black tracking-[-0.02em]',
    'font-mono font-medium tracking-[0.3em]',
    'font-heading font-condensed font-black tracking-[0.04em]',
    'font-sans font-light tracking-[0.18em]',
    'font-heading font-expanded font-extrabold italic tracking-[-0.01em]',
    'font-sans font-semibold tracking-[0.42em]',
    'font-heading font-black tracking-[-0.05em]',
    'font-mono font-normal tracking-[0.12em]'
]

const ClientStrip: FC<ClientStripProps> = ({ clients }): ReactNode => {
    if (!clients.length) return null

    const hasPlaceholders = clients.some((client) => client.isPlaceholder)

    return (
        <section id='clients' aria-labelledby='clients-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
            <div className='mx-auto grid max-w-[1440px] gap-10 px-5 py-16 md:px-10 md:py-20 lg:grid-cols-12 lg:items-center lg:gap-10'>
                <div data-reveal className='lg:col-span-4'>
                    <h2 id='clients-title' className='font-heading text-[clamp(1.75rem,2.6vw,2.4rem)] font-extrabold leading-[1.02] tracking-[-0.035em]'>
                        <span className='md:block'>Built for</span>{' '}
                        <span className='md:block'><Mark tone='dark'>ambitious</Mark> brands.</span>
                    </h2>
                    {hasPlaceholders && <PlaceholderTag tone='dark' className='mt-5'>Placeholder brands</PlaceholderTag>}
                </div>

                <ul className='grid grid-cols-2 border-l border-t border-cream/15 sm:grid-cols-4 lg:col-span-8'>
                    {clients.map((client, index) => {
                        const content = client.logo ? (
                            <span className='relative block h-8 w-28'>
                                <Image src={client.logo} alt={client.logoAlt || client.name} fill sizes='112px' className='object-contain' />
                            </span>
                        ) : (
                            <span className={cn('text-lg uppercase text-cream/85 transition-colors duration-300 group-hover:text-cream md:text-xl', WORDMARK_STYLES[index % WORDMARK_STYLES.length])}>
                                {client.name}
                            </span>
                        )

                        return (
                            <li key={client.id} data-reveal className='group flex h-24 items-center justify-center border-b border-r border-cream/15 px-4 md:h-28'>
                                {client.websiteUrl ? (
                                    <a href={client.websiteUrl} target='_blank' rel='noopener noreferrer' aria-label={client.name}>
                                        {content}
                                    </a>
                                ) : content}
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default ClientStrip
